'use client';

import {
  AnimatedDiv,
  AnimatePresence,
  autoUpdate,
  cn,
  Div,
  Placement,
  shift,
  useFloating,
} from '@scatterlab/zeta-core';
import { FC, memo, PropsWithChildren, ReactNode, Ref } from 'react';

import { TooltipCaretIcon } from './icons';

export type TooltipProps = PropsWithChildren<{
  content: ReactNode;
  active: boolean;
  placement: Placement;
  autoClose: boolean;
  containerClassName?: string;
}>;

const Tooltip: FC<TooltipProps> = ({
  content,
  active,
  placement,
  autoClose,
  children,
  containerClassName,
}) => {
  const { refs, floatingStyles, update } = useFloating({
    placement,
    strategy: 'fixed',
    transform: false,
    whileElementsMounted: autoUpdate,
    open: active,
    middleware: [shift()],
  });

  return (
    <Div
      className={cn('relative', containerClassName)}
      onLayout={update}
      ref={refs.setReference as Ref<HTMLDivElement>}
      collapsable={false}
    >
      {active && (
        <AnimatePresence>
          <AnimatedDiv
            ref={refs.setFloating as Ref<HTMLDivElement>}
            style={floatingStyles}
            initial={{ opacity: 1 }}
            animate={autoClose ? { opacity: 0 } : undefined}
            exit={autoClose ? { opacity: 0 } : undefined}
            transition={{ delay: 3, duration: 0.5 }}
            className={cn(
              'z-50 flex items-center',
              placement === 'top' && 'flex-col',
              placement === 'bottom' && 'flex-col-reverse',
              placement === 'left' && 'flex-row',
              placement === 'right' && 'flex-row-reverse',
            )}
          >
            <Div className="rounded-md bg-primary-400 px-1.5 py-1">
              {content}
            </Div>
            <TooltipCaretIcon
              className={cn(
                'h-[5px] w-2',
                placement === 'bottom' && 'rotate-180',
                placement === 'left' && 'ml-[-2px] -rotate-90',
                placement === 'right' && 'mr-[-2px] rotate-90',
              )}
            />
          </AnimatedDiv>
        </AnimatePresence>
      )}
      {children}
    </Div>
  );
};

export default memo(Tooltip);
